import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CalendarIcon,
  PencilIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../lib/api';
import type { NoteRecord } from '../hooks/useNotes';
import { FileAttachments } from '../components/FileAttachments';
import { RichTextEditor } from '../components/RichTextEditor';
import 'react-quill/dist/quill.snow.css';

// Strip HTML tags for plain text display
function stripHtml(html: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

async function fetchNote(
  clientId: string,
  noteId: string,
  accessToken: string | null | undefined
) {
  const notes = await apiRequest<NoteRecord[]>(
    `/api/clients/${clientId}/notes`,
    { method: 'GET' },
    accessToken
  );
  const note = notes.find((n) => n.id === noteId);
  if (!note) {
    throw new Error('Note not found');
  }
  return note;
}

export function NoteDetail() {
  const { clientId, noteId } = useParams<{ clientId: string; noteId: string }>();
  const navigate = useNavigate();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' } | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', content: '', tags: '' });

  const { data: note, isLoading, error } = useQuery<NoteRecord>({
    queryKey: ['note', clientId, noteId],
    enabled: Boolean(clientId && noteId && session),
    queryFn: () => fetchNote(clientId || '', noteId || '', session?.access_token),
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async () => {
      if (!session?.access_token || !clientId || !noteId) {
        throw new Error('Session expired. Please log in again.');
      }
      return apiRequest(
        `/api/clients/${clientId}/notes/${noteId}`,
        { method: 'DELETE' },
        session.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', clientId] });
      setToast({ message: 'Note deleted', type: 'success' });
      setTimeout(() => navigate(`/client/${clientId}?tab=notes`, { replace: true }), 1000);
    },
    onError: (error: unknown) => {
      setToast({
        message: error instanceof Error ? error.message : 'Failed to delete note',
        type: 'error'
      });
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async () => {
      if (!session?.access_token || !clientId || !noteId) {
        throw new Error('Session expired. Please log in again.');
      }
      return apiRequest(
        `/api/clients/${clientId}/notes/${noteId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            title: editForm.title.trim(),
            body: editForm.content.trim(),
            tags: editForm.tags.split(',').map(t => t.trim()).filter(Boolean),
          }),
        },
        session.access_token
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note', clientId, noteId] });
      queryClient.invalidateQueries({ queryKey: ['notes', clientId] });
      setIsEditing(false);
      setToast({ message: 'Note updated successfully', type: 'success' });
    },
    onError: (error: unknown) => {
      setToast({
        message: error instanceof Error ? error.message : 'Failed to update note',
        type: 'error'
      });
    },
  });

  const handleBack = () => {
    navigate(`/client/${clientId}?tab=notes`, { replace: true });
  };

  const handleDelete = () => {
    if (showDeleteConfirm) {
      deleteNoteMutation.mutate();
    } else {
      setShowDeleteConfirm(true);
    }
  };

  // Auto-dismiss toast
  React.useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B8E87] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading note...</p>
        </div>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-md">
          <XIcon className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#212121] mb-2">Note Not Found</h3>
          <p className="text-gray-600 mb-6">This note may have been deleted or you don't have access to it.</p>
          <button
            onClick={handleBack}
            className="px-6 py-2.5 bg-[#5B8E87] text-white rounded-xl font-medium hover:bg-[#4F7A74] transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5] pb-6">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-20">
        <div className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-800"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-semibold text-[#212121]">Note Details</h1>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {isEditing ? (
              <button
                onClick={() => setIsEditing(false)}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                title="Cancel Edit"
              >
                <XIcon className="w-5 h-5" />
              </button>
            ) : (
              <>
                <button
                  onClick={() => {
                    setIsEditing(true);
                    setEditForm({
                      title: note.title,
                      content: note.body,
                      tags: note.tags.join(', ')
                    });
                  }}
                  className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  title="Edit Note"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={handleDelete}
                  className={`p-2 rounded-lg transition-colors ${
                    showDeleteConfirm
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-red-50 text-red-500 hover:bg-red-100'
                  }`}
                  title={showDeleteConfirm ? 'Confirm Delete' : 'Delete Note'}
                >
                  <Trash2Icon className="w-5 h-5" />
                </button>
                {showDeleteConfirm && (
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    title="Cancel"
                  >
                    <XIcon className="w-5 h-5" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4">
          <div className={`rounded-xl border px-4 py-3 text-sm shadow-lg ${
            toast.type === 'error'
              ? 'bg-red-50 text-red-700 border-red-200'
              : 'bg-green-50 text-green-700 border-green-200'
          }`}>
            {toast.message}
          </div>
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* Note Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
          {isEditing ? (
            // Edit mode UI
            <div className="space-y-4">
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none text-2xl font-bold"
                placeholder="Note title"
              />

              <RichTextEditor
                value={editForm.content}
                onChange={(value) => setEditForm(prev => ({ ...prev, content: value }))}
                placeholder="Note content..."
                minHeight="300px"
              />

              <input
                type="text"
                value={editForm.tags}
                onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none"
                placeholder="Tags (comma separated)"
              />

              <div className="flex gap-3">
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateNoteMutation.mutate()}
                  disabled={updateNoteMutation.isPending || !editForm.title.trim() || !editForm.content.trim()}
                  className="flex-1 px-6 py-3 bg-[#5B8E87] text-white rounded-xl font-medium hover:bg-[#4F7A74] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {updateNoteMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            // View mode UI
            <>
              {/* Title */}
              <h2 className="text-2xl font-bold text-[#212121] mb-2">{note.title}</h2>

              {/* Metadata */}
              <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                <span>{formatDate(note.created_at)}</span>
                {note.updated_at && note.updated_at !== note.created_at && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span>Updated {formatDate(note.updated_at)}</span>
                  </>
                )}
              </div>

              {/* Appointment Link */}
              {note.appointment_id && (
                <button
                  onClick={() => navigate(`/appointment/${note.appointment_id}`)}
                  className="mb-4 inline-flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                >
                  <CalendarIcon className="w-4 h-4" />
                  <span>View linked appointment</span>
                </button>
              )}

              {/* Content - Render HTML instead of stripping it */}
              <div
                className="prose prose-sm max-w-none text-base text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: note.body }}
              />

              {/* Tags */}
              {note.tags && note.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-gray-200">
                  {note.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="text-sm font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-700"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* File Attachments */}
              {note.file_urls && note.file_urls.length > 0 && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <FileAttachments
                    fileUrls={note.file_urls}
                    fileNames={note.file_names}
                    fileSizes={note.file_sizes}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Delete Confirmation Message */}
        {showDeleteConfirm && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <p className="text-sm text-red-700 font-medium">
              Click the delete button again to confirm deletion. This action cannot be undone.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
